import { type FC, useEffect, useState } from 'react';
import Spacer from 'src/components-v2/Layout/Spacer/Spacer';
import Footer from 'src/components-v3/Footer';
import APISection from 'src/components-v3/Homepage/APISection/APISection';
import Carousel from 'src/components-v3/Homepage/Carousel/Carousel';
import Contact from 'src/components-v3/Homepage/Contact/Contact';
import CtaCallout from 'src/components-v3/Homepage/CtaCallout/CtaCallout';
import {
  desktopPreload,
  mobilePreload,
} from 'src/components-v3/Homepage/Hero/Ben';
import Hero from 'src/components-v3/Homepage/Hero/Hero';
import { ToggleProvider } from 'src/components-v3/Homepage/Hero/Toggle';
import NetworkSection from 'src/components-v3/Homepage/Network/Network';
import Portal from 'src/components-v3/Homepage/Portal/Portal';
import SolutionsBento from 'src/components-v3/Homepage/SolutionsBento/SolutionsBento';
import { useDeviceSize } from 'src/hooks';
import { CriticalRenderProvider } from 'src/hooks/useCriticalRenderContext';
import useHasMouse from 'src/hooks/useHasMouse';
import { Locales } from 'src/lib/constants';
import MainTemplate from 'src/templates/MainTemplate/MainTemplate';
import styled from 'threads5/styles/styled';

const metaData = {
  'meta-title': 'Plaid: Enabling all companies to build fintech solutions',
  'meta-description':
    'Plaid helps all companies build fintech solutions by making it easy, safe and reliable for people to connect their financial data to apps and services.',
  hasForeignContentNotification: true,
  hasFooter: true,
  hasNavigation: true,
  noIndex: false,
  preloadImages: [...desktopPreload, ...mobilePreload],
  excludeBrandFromMetaTitle: true,
};

const orgData = {
  '@context': 'https://schema.org',
  '@type': 'organization',
  '@id': 'https://plaid.com/#organization',
  name: 'Plaid',
  description:
    "Plaid enables you to tap into the industry's largest network of banks, fintechs, and consumers. Beyond connecting financial accounts, Plaid helps businesses onboard new customers, reduce fraud, and increase the lifetime value of customers across the user journey.",
  url: 'https://plaid.com/',
  logo: {
    '@type': 'ImageObject',
    url: 'https://security.plaid.com/api/share/f3892b6a-4afc-4cc0-ab7c-9a5363e37354/logo.png',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1098 Harrison St.',
    addressLocality: 'San Francisco',
    addressRegion: 'CA',
    postalCode: '94103',
    addressCountry: 'US',
  },
  sameAs: [
    'https://www.linkedin.com/company/plaid-',
    'https://en.wikipedia.org/wiki/Plaid_Inc.',
    'https://x.com/Plaid',
    'https://x.com/PlaidDev',
    'https://www.youtube.com/c/PlaidInc',
    'https://www.instagram.com/plaid/',
  ],
};

const StyledContent = styled('div')(({ theme }) => {
  return {
    background: theme.palette['gradient/background/subtle/elizabeth'],
  };
});

const Homepage: FC = () => {
  const hasMouse = useHasMouse();
  const { width } = useDeviceSize();
  const [isMobile, setIsMobile] = useState(false);

  // Disable homepage guillochés on mobile to prevent iOS Safari crashes incurred by using 5 guillochés on the page
  useEffect(() => {
    setIsMobile(width < 1024 || !hasMouse);
  }, [hasMouse, width]);

  return (
    <MainTemplate
      {...metaData}
      structuredData={orgData}
      locale={Locales.EN_US}
      cmsTheme='web 3.0'
      sx={{
        '&#main-content': {
          overflowX: 'unset',
          overflowY: 'unset',
          background:
            'linear-gradient(180deg, #F0F2FD 0%, #FAF7FF 35.5%, #FAF7FF 47%, #FAF7FF 68.5%, #EAEDFC 99.5%)',
        },
      }}
      hasFooter={false}
    >
      <CriticalRenderProvider value={{ assetQuerySelector: '.critical-asset' }}>
        <ToggleProvider>
          <Hero />
        </ToggleProvider>
        <StyledContent>
          <SolutionsBento />
          <CtaCallout />
          <NetworkSection isGuillocheDisabled={isMobile} />
          <APISection isGuillocheDisabled={isMobile} />
          <Carousel isGuillocheDisabled={isMobile} />
          <ToggleProvider>
            <Contact>
              <Spacer height={80} />
              <Portal />
            </Contact>
          </ToggleProvider>
        </StyledContent>
      </CriticalRenderProvider>
      <Footer roundedTop overlap />
    </MainTemplate>
  );
};

export default Homepage;
